import React from 'react';
import { type MainMapView } from '../mapUtils';

interface BuildingOperationalRow {
  id: number;
  name: string;
  typeLabel: string;
  level: number;
  vehiclesCount: number;
  vehiclesReady: number;
  vehicleCapacity: number;
  vehicleLoadPercent: number;
  personnelCount: number;
  personnelActive: number;
  personnelSlots: number;
  personnelOccupied: number;
  personnelLoadPercent: number;
  xp: number;
  xpRequired: number;
  xpPercent: number;
  isPersonnelUpgradeRunning: boolean;
  isVehicleUpgradeRunning: boolean;
}

interface BuildingTypeBreakdownRow {
  typeName: string;
  count: number;
  vehicles: number;
  personnel: number;
}

interface BuildingsViewProps {
  buildingOperationalRows: BuildingOperationalRow[];
  buildingTypeBreakdown: BuildingTypeBreakdownRow[];
  handleBuildingsClick: () => void;
  setMainView: (view: MainMapView) => void;
  openDedicatedMainView: (view: MainMapView) => void;
  openInterventionsSynoptic: () => void;
}

const BuildingsView: React.FC<BuildingsViewProps> = ({
  buildingOperationalRows,
  buildingTypeBreakdown,
  handleBuildingsClick,
  openDedicatedMainView,
  openInterventionsSynoptic,
}) => (
  <div className="grid h-full min-h-0 grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.45fr),minmax(0,1fr)]">
    <section className="min-h-0 rounded-lg border border-slate-700/75 bg-slate-900/90 p-3 shadow-xl shadow-black/30">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-sky-200">Vue bâtiments avancée</h2>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={openInterventionsSynoptic}
            className="rounded border border-red-500/45 bg-red-600/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-red-100 hover:bg-red-600/30"
          >
            Synoptique
          </button>
          <button
            type="button"
            onClick={() => openDedicatedMainView('buildings')}
            className="rounded border border-slate-600/70 bg-slate-800/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-700/80"
          >
            Ouvrir en page
          </button>
          <button
            type="button"
            onClick={handleBuildingsClick}
            className="rounded-md border border-sky-500/55 bg-sky-600/20 px-2.5 py-1 text-[10px] font-semibold uppercase text-sky-100 hover:bg-sky-600/30"
          >
            Gérer les bâtiments
          </button>
        </div>
      </div>
      <div className="mb-3 grid grid-cols-2 gap-2 text-[11px] xl:grid-cols-4">
        <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">Bâtiments actifs</p>
          <p className="mt-1 text-lg font-semibold text-slate-100">{buildingOperationalRows.length}</p>
        </div>
        <div className="rounded border border-emerald-500/35 bg-emerald-950/20 p-2">
          <p className="text-[10px] uppercase tracking-wide text-emerald-300">Véhicules rattachés</p>
          <p className="mt-1 text-lg font-semibold text-emerald-200">
            {buildingOperationalRows.reduce((sum, row) => sum + row.vehiclesCount, 0)}
          </p>
        </div>
        <div className="rounded border border-sky-500/35 bg-sky-950/20 p-2">
          <p className="text-[10px] uppercase tracking-wide text-sky-300">Personnel rattaché</p>
          <p className="mt-1 text-lg font-semibold text-sky-200">
            {buildingOperationalRows.reduce((sum, row) => sum + row.personnelCount, 0)}
          </p>
        </div>
        <div className="rounded border border-amber-500/35 bg-amber-950/20 p-2">
          <p className="text-[10px] uppercase tracking-wide text-amber-300">Chantiers en cours</p>
          <p className="mt-1 text-lg font-semibold text-amber-200">
            {buildingOperationalRows.filter((row) => row.isPersonnelUpgradeRunning || row.isVehicleUpgradeRunning).length}
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-md border border-slate-700/70 bg-slate-950/80">
        <div className="grid grid-cols-[minmax(0,1.15fr),88px,80px,88px,78px,82px,82px] border-b border-slate-700/70 bg-slate-900/70 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">
          <span>Bâtiment</span>
          <span className="text-center">Niveau</span>
          <span className="text-center">Véhic.</span>
          <span className="text-center">Pers.</span>
          <span className="text-center">Charge V</span>
          <span className="text-center">Charge P</span>
          <span className="text-center">XP</span>
        </div>
        <div className="max-h-[58vh] overflow-y-auto">
          {buildingOperationalRows.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-slate-500">Aucun bâtiment disponible.</p>
          ) : (
            buildingOperationalRows.map((row) => (
              <div key={row.id} className="border-b border-slate-800/65 px-2 py-2 text-[11px] last:border-b-0">
                <div className="grid grid-cols-[minmax(0,1.15fr),88px,80px,88px,78px,82px,82px] items-center gap-1">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-100">{row.name}</p>
                    <p className="truncate text-[10px] text-slate-400">{row.typeLabel}</p>
                  </div>
                  <span className="text-center font-semibold text-slate-200">Niv. {row.level}</span>
                  <span className="text-center text-slate-300">{row.vehiclesCount}/{Math.max(0, row.vehicleCapacity)}</span>
                  <span className="text-center text-slate-300">{row.personnelOccupied}/{Math.max(0, row.personnelSlots)}</span>
                  <span className={`text-center font-semibold ${row.vehicleLoadPercent >= 85 ? 'text-red-300' : row.vehicleLoadPercent >= 65 ? 'text-amber-300' : 'text-emerald-300'}`}>
                    {row.vehicleLoadPercent}%
                  </span>
                  <span className={`text-center font-semibold ${row.personnelLoadPercent >= 85 ? 'text-red-300' : row.personnelLoadPercent >= 65 ? 'text-amber-300' : 'text-emerald-300'}`}>
                    {row.personnelLoadPercent}%
                  </span>
                  <span className="text-center text-slate-300">{row.xp}/{row.xpRequired}</span>
                </div>
                <div className="mt-1.5 grid grid-cols-1 gap-1 text-[10px] md:grid-cols-3">
                  <p className="rounded border border-slate-700/70 bg-slate-900/70 px-2 py-1 text-slate-300">
                    Flotte prête : <span className="font-semibold text-emerald-300">{row.vehiclesReady}</span>
                  </p>
                  <p className="rounded border border-slate-700/70 bg-slate-900/70 px-2 py-1 text-slate-300">
                    Personnel actif : <span className="font-semibold text-sky-300">{row.personnelActive}</span>
                  </p>
                  <p className="rounded border border-slate-700/70 bg-slate-900/70 px-2 py-1 text-slate-300">
                    Travaux :
                    <span className={`ml-1 font-semibold ${row.isPersonnelUpgradeRunning || row.isVehicleUpgradeRunning ? 'text-amber-300' : 'text-emerald-300'}`}>
                      {row.isPersonnelUpgradeRunning || row.isVehicleUpgradeRunning ? 'EN COURS' : 'RAS'}
                    </span>
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
    <section className="grid min-h-0 grid-rows-[auto,auto,1fr] gap-3">
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Charge moyenne véhicules</p>
          <p className="mt-1 text-lg font-semibold text-sky-300">
            {buildingOperationalRows.length > 0
              ? Math.round(buildingOperationalRows.reduce((sum, row) => sum + row.vehicleLoadPercent, 0) / buildingOperationalRows.length)
              : 0}%
          </p>
        </div>
        <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Charge moyenne personnel</p>
          <p className="mt-1 text-lg font-semibold text-sky-300">
            {buildingOperationalRows.length > 0
              ? Math.round(buildingOperationalRows.reduce((sum, row) => sum + row.personnelLoadPercent, 0) / buildingOperationalRows.length)
              : 0}%
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
        <p className="mb-2 text-[10px] uppercase tracking-[0.12em] text-amber-300">Bâtiments sous tension</p>
        <div className="max-h-[18vh] space-y-1.5 overflow-y-auto text-[11px]">
          {buildingOperationalRows.filter((row) => row.vehicleLoadPercent >= 80 || row.personnelLoadPercent >= 80).slice(0, 6).map((row) => (
            <div key={`stress-${row.id}`} className="rounded border border-amber-500/35 bg-amber-950/20 px-2 py-1.5">
              <p className="truncate font-semibold text-amber-100">{row.name}</p>
              <p className="text-[10px] text-amber-200">
                Véhicules {row.vehicleLoadPercent}% - Personnel {row.personnelLoadPercent}%
              </p>
            </div>
          ))}
          {buildingOperationalRows.filter((row) => row.vehicleLoadPercent >= 80 || row.personnelLoadPercent >= 80).length === 0 ? (
            <p className="rounded border border-emerald-500/30 bg-emerald-950/20 px-2 py-1 text-emerald-200">
              Aucune saturation critique détectée.
            </p>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
        <p className="mb-2 text-[10px] uppercase tracking-[0.12em] text-slate-400">Répartition par service</p>
        <div className="max-h-[36vh] space-y-1.5 overflow-y-auto text-xs">
          {buildingTypeBreakdown.map((row) => (
            <div key={row.typeName} className="rounded border border-slate-700/70 bg-slate-950/75 px-2 py-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-slate-200">{row.typeName}</span>
                <span className="text-sky-300">{row.count} site(s)</span>
              </div>
              <p className="mt-0.5 text-[10px] text-slate-400">
                {row.vehicles} véhicules - {row.personnel} agents
              </p>
            </div>
          ))}
          {buildingTypeBreakdown.length === 0 ? (
            <p className="text-slate-500">Aucun service disponible.</p>
          ) : null}
        </div>
      </div>
    </section>
  </div>
);

export default BuildingsView;
